import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { orderApi } from '#services/order';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface OrderListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: string;
  orderBy: string;
  pageIndex: number;
  pageSize: number;
  rows: unknown[];
  rowCount: number;
  selected: Array<string | number | undefined>;
  skip: boolean;
}

const initialState: OrderListState = {
  filter: '',
  dateRange: buildTimeRange(DEFAULT_TIME_RANGE_PRESET),
  filterKey: 'orderId',
  headerFilters: {},
  order: 'desc',
  orderBy: 'id',
  pageIndex: 0,
  pageSize: 30,
  rows: [],
  rowCount: 0,
  selected: [],
  skip: false,
};

export const orderListSlice = createSlice({
  name: 'orderList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
      state.pageIndex = 0;
      state.skip = true; // Skip query when just typing in the filter box
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
      state.skip = true; // Skip query when only changing filter type
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.headerFilters = action.payload;
      state.skip = false;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
      state.skip = false;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
      state.skip = false;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
      state.skip = false;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
      state.skip = false;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
      state.skip = false;
    },
    setSelected(state, action: PayloadAction<Array<string | number | undefined>>) {
      const { payload } = action;
      state.selected = payload;
      state.skip = false;
    },
    setSkipFlag(state, action: PayloadAction<boolean>) {
      state.skip = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(orderApi.endpoints.getOrders.matchFulfilled, (state, action) => {
        const { page, rows, total } = action.payload as {
          page: number;
          rows: unknown[];
          total: number;
        };
        state.rows = rows;
        state.rowCount = total;
        state.pageIndex = page;
        // dont clear the selected data,
        // the selected states should be remembered for the crossing page selection
        // state.selected = [];
      });
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
  setSelected,
  setSkipFlag,
} = orderListSlice.actions;

export default orderListSlice.reducer;
